/**
 * Files handed to the app from outside it, rather than picked or dropped:
 *
 *  - **Open With** on an installed desktop web app (Chrome, Edge), through
 *    the manifest's `file_handlers` and the Launch Queue API;
 *  - **the share sheet** on Android, through the manifest's `share_target`,
 *    which the service worker receives (`service-worker.ts`, `receiveShare`).
 *
 * iOS has neither for a web app: a Home Screen app cannot be a share target
 * or open a file from the Files app. There, files come in through the Import
 * button, which opens the Files picker. `docs/DESKTOP.md` covers what a
 * native iOS build would add.
 */

/** Must match `SHARE_CACHE` in `sw-routing.ts`; not imported, because the worker cannot share a chunk with the app. */
export const SHARED_FILES_CACHE = 'fdv-shared-files';

interface LaunchParams {
  files: readonly { getFile: () => Promise<File> }[];
}
interface LaunchQueue {
  setConsumer: (consumer: (params: LaunchParams) => void) => void;
}

export function consumeLaunchQueue(open: (files: File[]) => void): void {
  const queue = (globalThis as { launchQueue?: LaunchQueue }).launchQueue;
  queue?.setConsumer((params) => {
    if (params.files.length === 0) {
      return;
    }
    void Promise.all(params.files.map((handle) => handle.getFile())).then(open);
  });
}

/**
 * The files the share sheet delivered, if this page load is the one it
 * opened. Collected once: the cache is emptied and the query removed from
 * the address, so a reload does not import them twice.
 */
export async function collectSharedFiles(): Promise<File[]> {
  const url = new URL(window.location.href);
  if (url.searchParams.get('shared') !== '1' || typeof caches === 'undefined') {
    return [];
  }
  url.searchParams.delete('shared');
  window.history.replaceState(window.history.state, '', url.href);

  const cache = await caches.open(SHARED_FILES_CACHE);
  const files: File[] = [];
  for (const request of await cache.keys()) {
    const response = await cache.match(request);
    if (response === undefined) {
      continue;
    }
    const name = decodeURIComponent(response.headers.get('x-filename') ?? 'shared-file');
    files.push(
      new File([await response.blob()], name, { type: response.headers.get('content-type') ?? '' }),
    );
  }
  await caches.delete(SHARED_FILES_CACHE);
  return files;
}
